import { m, useReducedMotion } from "framer-motion";

/**
 * The warm-in-the-top-left, cool-in-the-bottom-right wash that keeps a flat
 * ink hero from reading as a black rectangle — the countdown banner on the
 * calendar and the samling details page's masthead both sit on it.
 *
 * Two things animate here: the two gradients themselves, and a handful of
 * small sparks scattered through the warm corner. An earlier version was
 * the gradients alone, breathing together as a single opacity fade — which
 * read as the *card* pulsing rather than as light doing anything, a box
 * quietly inflating and deflating. The sparks add texture on top of that:
 * a few small points of a genuinely different size and brightness each,
 * rising and fading on their own, rather than one smooth wash standing in
 * for a fire.
 *
 * The rule that holds all of it together, gradients and sparks alike:
 * nothing shares a timeline with anything else. Every animated value runs
 * on its own duration, chosen so no two of them land on a shared small
 * common multiple. Sharing a timeline is what gives a loop away — things
 * that swell, hold and fade together read as one mechanism on a cycle, and
 * the eye catches that unison almost immediately. With nothing lining up,
 * the whole thing keeps landing on arrangements it hasn't quite shown
 * before instead of visibly repeating — which is what actually reads as
 * firelight rather than as an animation.
 *
 * Everything stays small on purpose — a few percent of scale on the
 * gradients, a handful of pixels of drift, sparks a few pixels across at
 * well under half opacity — because this sits behind text people are
 * trying to read, not a hero image asking to be looked at.
 * `prefers-reduced-motion` drops all of it, sparks included, back to the
 * two gradients sitting still.
 *
 * `transformOrigin` on each gradient is pinned to its own centre (not the
 * element's) so scaling reads as the glow itself swelling from where it's
 * actually anchored, rather than the whole layer zooming from its middle.
 */

interface Spark {
  top: string;
  left: string;
  /** Diameter in px — sparks are small enough that whole pixels matter. */
  size: number;
  /** How bright this one gets at the peak of its own cycle — what makes
   * some read as a closer ember and others as a fainter one further back. */
  peak: number;
  duration: number;
  delay: number;
}

// Scattered around the ember gradient's own origin (both callers anchor it
// within a couple of percent of 8–10%, 4–8%) rather than tied to either
// exactly — each one's distance from that origin roughly doubled to match
// the gradient's own reach below, so the sparks still read as texture
// within the glow rather than a tight little cluster lost inside a much
// bigger wash of light. Sizes and peaks vary on purpose: a field of
// identical dots would read as a pattern, not as embers.
const SPARKS: Spark[] = [
  { top: "12%", left: "3%", size: 3, peak: 0.65, duration: 6.4, delay: 0 },
  { top: "2%", left: "27%", size: 2, peak: 0.4, duration: 8.1, delay: 1.6 },
  { top: "34%", left: "11%", size: 4, peak: 0.55, duration: 7.2, delay: 3.1 },
  { top: "20%", left: "43%", size: 2.5, peak: 0.35, duration: 9.4, delay: 0.6 },
  { top: "48%", left: "2%", size: 3.5, peak: 0.5, duration: 6.9, delay: 2.3 },
];

const SPARK_COLOR = "255,205,130";

function Sparks() {
  return (
    <>
      {SPARKS.map((spark, i) => (
        <m.div
          key={i}
          aria-hidden
          className="absolute rounded-full"
          style={{
            top: spark.top,
            left: spark.left,
            width: spark.size,
            height: spark.size,
            background: `radial-gradient(circle, rgba(${SPARK_COLOR},0.95) 0%, rgba(${SPARK_COLOR},0.5) 45%, transparent 75%)`,
          }}
          // Rises and fades like heat off an ember rather than blinking on
          // and off: both the fade and the drift start and end each cycle
          // at the same resting value, so there's no snap at the seam
          // where one loop hands off to the next.
          animate={{
            opacity: [spark.peak * 0.25, spark.peak, spark.peak * 0.4, spark.peak * 0.8, spark.peak * 0.25],
            scale: [0.8, 1.2, 0.9, 1.05, 0.8],
            y: [0, -4, -7, -3, 0],
          }}
          transition={{
            duration: spark.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: spark.delay,
          }}
        />
      ))}
    </>
  );
}

export function EmberGlow({
  ember,
  emberOrigin = "10% 8%",
  corner,
  cornerOrigin = "100% 100%",
  borderRadius = "",
}: {
  /** The warm gradient's full CSS `background` value. */
  ember: string;
  emberOrigin?: string;
  /** The cool corner gradient's full CSS `background` value. */
  corner: string;
  cornerOrigin?: string;
  /** Tailwind border-radius class to clip gradients to parent shape. */
  borderRadius?: string;
}) {
  const reduceMotion = useReducedMotion();
  const ease = "easeInOut" as const;
  const repeat = Infinity;

  return (
    <>
      <m.div
        aria-hidden
        className={`absolute inset-0 ${borderRadius}`}
        style={{ background: ember, transformOrigin: emberOrigin }}
        animate={
          reduceMotion
            ? undefined
            : {
                opacity: [0.85, 1, 0.9, 1, 0.85],
                scale: [1, 1.045, 0.985, 1.025, 1],
                x: [0, 5, -3, 2, 0],
                y: [0, -3, 2, -2, 0],
              }
        }
        transition={
          reduceMotion
            ? undefined
            : {
                opacity: { duration: 11, repeat, ease },
                scale: { duration: 13.5, repeat, ease },
                x: { duration: 9.5, repeat, ease },
                y: { duration: 12.5, repeat, ease, delay: 0.4 },
              }
        }
      />
      <m.div
        aria-hidden
        className={`absolute inset-0 ${borderRadius}`}
        style={{ background: corner, transformOrigin: cornerOrigin }}
        animate={
          reduceMotion
            ? undefined
            : {
                opacity: [0.9, 0.78, 1, 0.88, 0.9],
                scale: [1, 1.02, 0.995, 1.03, 1],
              }
        }
        transition={
          reduceMotion
            ? undefined
            : {
                // Well clear of the ember layer's own durations above, and
                // started mid-cycle rather than from zero, so the two
                // gradients are never in step with each other either.
                opacity: { duration: 16, repeat, ease, delay: 2.4 },
                scale: { duration: 18.5, repeat, ease, delay: 1.1 },
              }
        }
      />
      {/* Only while there's motion to show them off — a spark that never
          fades or drifts is just a small stray dot, not an ember. */}
      {!reduceMotion && <Sparks />}
    </>
  );
}
